import React, { useCallback, useEffect, useState } from "react";
import { FaCashRegister, FaDownload, FaStore } from "react-icons/fa";
import { FaRegCreditCard } from "react-icons/fa6";
import { BsCashStack } from "react-icons/bs";
import { Link } from "react-router-dom";
import VerificationButtonLink from "../components/VerificationButtonLink";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import axios from "axios";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorMessage from "@/components/ErrorMessage";
import toast from "react-hot-toast";
import { PayoutHistory, PayoutStatus, WalletData } from "@/types/globalTypes";
import { AppConfig } from "@/utils/AppConfig";
import { RootState } from "@/store/store";
import PaginationControls from "@/components/PaginationControls";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/utils/helpers";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

const payoutsStatusConfigs: Record<PayoutStatus, { color: string, labelEn: string, labelAR: string }> = {
  paid: { color: 'bg-green-400', labelEn: "paid", labelAR: 'مدفوع' },
  failed: { color: 'bg-red-400', labelEn: "failed", labelAR: 'فشل' },
  initiated: { color: 'bg-blue-400', labelEn: "initiated", labelAR: 'تم البدء' },
  canceled: { color: 'bg-gray-400', labelEn: "canceled", labelAR: 'أُلغي' },
  queued: { color: 'bg-orange-400', labelEn: "queued", labelAR: 'في الانتظار' },
  returned: { color: 'bg-purple-400', labelEn: "returned", labelAR: 'مُرتجع' },
};
const Wallet: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const [walletData, setWalletData] = React.useState<WalletData>(
    {} as WalletData,
  );
  const [withdrowOpen, setWithdrowOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [needRefetch, setNeedRefetch] = React.useState(false);

  useLockBodyScroll(withdrowOpen);
  //cashier Logic
  const apps = useSelector((state: RootState) => state.apps.AppsData);
  const cashierApp = apps?.find((app) => app.name === "cashier");
  const isCashierInstalledAndActive =
    cashierApp?.available && cashierApp?.installed;

  // Pagenation 
  const [payoutHistory, setPayoutHistory] = useState<PayoutHistory[]>([]);
  const [pageNum, setPageNum] = useState(10);
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const handleSetPageNumber = (page: number) => {
    setPageNum(page);
    setCurrentPage(1);
  };



  const getPayoutTransactions = useCallback(async () => {
    const url = `${baseUrl}/api/dashboard/settings/wallet/payouts?page=${currentPage}&length=${pageNum}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `${token}`,
        },
      });

      if (!response.data.success) {
        const errMessage =
          response.data.error ||
          (t("walletSettings.unexpectedError") as string);
        setError(errMessage);
        toast.error(errMessage);
        return;
      }

      setPayoutHistory(response.data.payoutHistory);
      setTotalItems(response.data.pagination.totalItems);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data.error ||
          (t("transactions.fetchFailed") as string);
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl, currentPage, pageNum, t, token]);

  const getWalletData = useCallback(async () => {
    setLoading(true);
    setNeedRefetch(false);
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/settings/wallet`,
        method: "GET",
        headers: {
          Authorization: token,
        },
      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        setWalletData(response.data);
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data.error ||
          t("walletSettings.unexpectedError");
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token, t]);

  useEffect(() => {
    if (!token) return;

    getWalletData();
    getPayoutTransactions()
  }, [getPayoutTransactions, getWalletData, needRefetch, token]);

  const validationSchema = Yup.object({
    iban: Yup.string().required(
      t("walletSettings.ibanRequired") as string,
    ),
  });

  const formik = useFormik({
    initialValues: {
      iban: walletData?.iban || "",
    },
    validationSchema,
    onSubmit: (values) => {
      const handleWithdrawCurrnetBalanceRequest = async () => {
        const url = `${baseUrl}/api/dashboard/requests?locale=${lang}`;
        const loadingToast = toast.loading(
          t("walletSettings.sendingRequest"
          ) as string,
        );

        try {
          const response = await axios.post(
            url,
            {
              type: "payout",
              iban: values.iban,
            },
            {
              headers: {
                Authorization: token,
                "Content-Type": "application/json",
              },
            },
          );

          if (!response.data.success) {
            toast.error(
              response.data.error ||
              (t(
                "walletSettings.failedRequest",
              ) as string),
            );
            return;
          }

          toast.success(
            t(
              "walletSettings.successRequest"
            ) as string,
          );
          setWithdrowOpen(false);
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data.error ||
              t("walletSettings.unexpectedError");
            toast.error(message);
          }
        } finally {
          toast.dismiss(loadingToast);
        }
      };
      handleWithdrawCurrnetBalanceRequest();
    },
    enableReinitialize: true,
  });

  if (loading)
    return (
      <div className="">
        <Skeleton className="mt-20 h-[100px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        <div className="mt-10 flex flex-wrap justify-start gap-5">
          <Skeleton className="h-[260px] w-[420px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[260px] w-[420px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[260px] w-[420px] dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </div>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      {withdrowOpen && (
        <div className="absolute left-0 top-0 z-[9999] flex h-full w-full items-center justify-center">
          <div
            onClick={() => setWithdrowOpen(false)}
            className="layer absolute h-full w-full bg-slate-800 bg-opacity-30"
          >
            {" "}
          </div>

          <form
            onSubmit={formik.handleSubmit}
            className="z-50 w-[300px] overflow-hidden rounded-xl bg-white md:w-[500px]"
          >
            <header className="bg-skyblue py-4 text-center font-bold">
              {t("walletSettings.withdrawRequest")}
            </header>
            <div className="my-5 flex flex-col gap-2">
              <div className="flex flex-col gap-3 px-5 py-2">
                <h1 className="rounded-md bg-gray-200 px-2 py-1 text-center">
                  {t(
                    "walletSettings.confirmWithdraw"
                    , { amount: walletData?.currentBalance, currency: lang === "ar" ? AppConfig.currency.ar : AppConfig.currency.en })}
                </h1>
                <label htmlFor="" className="font-semibold text-sidebar">
                  {t("walletSettings.verifyIban")}
                </label>
                <input
                  type="text"
                  name="iban"
                  readOnly
                  id="iban"
                  placeholder={
                    t("walletSettings.verifyIban") as string
                  }
                  value={formik.values.iban}
                  onChange={formik.handleChange}
                  className="rounded-md border border-gray-300 px-3 py-2"
                />
                {formik.touched.iban && formik.errors.iban && (
                  <div className="text-red-500">{formik.errors.iban}</div>
                )}
              </div>
            </div>
            <footer className="flex justify-between px-5 py-5">
              <button
                type="submit"
                className="rounded-md bg-sidebar px-5 py-2 text-skyblue"
              >
                {t("walletSettings.confirm")}
              </button>
              <button
                type="button"
                onClick={() => setWithdrowOpen(false)}
                className="rounded-md bg-slate-500 px-5 py-2 text-white"
              >
                {t("walletSettings.cancel")}
              </button>
            </footer>
          </form>
        </div>
      )}
      {loading ? (
        <>
          <div className="mt-20 w-full">
            <Skeleton className="h-[100px] w-full" />
            <div className="my-10 flex h-fit w-full flex-wrap justify-center gap-5 md:justify-start">
              <Skeleton className="h-[300px] w-[450px] rounded-md px-4 py-4" />
              <Skeleton className="h-[300px] w-[450px] rounded-md px-4 py-4" />
              <Skeleton className="h-[300px] w-[450px] rounded-md px-4 py-4" />
            </div>
            <div className="mt-20">
              <Skeleton className="h-[100px] w-full px-5" />
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <h1 className="self-start text-2xl font-semibold">
              {t("walletSettings.walletSettings")}
            </h1>

            <section className="wallet my-10">
              <div className="flex w-full flex-col">
                <VerificationButtonLink
                  verificationStatus={walletData?.businessVerificationStatus}
                />
                {/* Cards Container */}
                <div className="store-info mb-10 flex h-fit flex-wrap justify-center gap-5 md:justify-start">
                  {/* Store Credit Card */}
                  <div className="store-credit main-border h-[260px] w-[420px] space-y-2 rounded-md border border-gray-300 px-4 py-4">
                    <div className="card-header items flex items-center gap-3 text-gray-400">
                      <FaRegCreditCard className="text-[19px]" />
                      <h3 className="m-0 font-semibold text-gray-400">
                        {t("walletSettings.storeCredit")}
                      </h3>
                    </div>

                    <div className="card-body !mt-5 flex flex-col items-center justify-center gap-2">
                      <FaRegCreditCard className="text-[35px] text-gray-400" />

                      {+walletData?.currentBalance === 0 ? (
                        <p className="text-center text-[23px] font-semibold text-gray-400">
                          {t(
                            "walletSettings.noBalance"
                          )}
                        </p>
                      ) : (
                        <span
                          dir="rtl"
                          className="flex items-center justify-center gap-2 text-xl font-bold text-sidebar"
                        >
                          {walletData?.currentBalance}
                          <span>
                            {AppConfig.currency.icon("currentcolor", 18)}
                          </span>
                        </span>
                      )}

                      <div className="my-2 flex gap-3">
                        <button
                          disabled={
                            +walletData?.currentBalance <= 0 ||
                            walletData?.businessVerificationStatus !==
                            "verified"
                          }
                          onClick={() => setWithdrowOpen(true)}
                          className="text-colors-primary rounded-lg border bg-sidebar px-4 py-2 font-semibold text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-300"
                        >
                          {t("walletSettings.withdrawBalance")}
                        </button>
                      </div>
                    </div>
                    <div className="card-footer text-center">
                      <Link
                        to="/settings/store-balance-history"
                        className="hover:text-colors-primary flex items-center justify-center gap-1 text-[15px] text-sidebar underline transition-colors duration-100"
                      >
                        <FaStore /> {t("walletSettings.transferHistory")}
                      </Link>
                    </div>
                  </div>

                  {/* Store Cash */}
                  <div className="store-cash main-border h-[260px] w-[420px] space-y-6 rounded-md border border-gray-300 px-4 py-4">
                    <div className="card-header items flex items-center gap-3 text-gray-400">
                      <BsCashStack className="text-[19px]" />
                      <h3 className="m-0 font-semibold text-gray-400">
                        {t("walletSettings.storeCash")}
                      </h3>
                    </div>
                    <div className="card-body flex flex-col items-center justify-center gap-2">
                      <BsCashStack className="text-[35px] text-gray-400" />
                      <p className="p-3 text-center text-[30px] font-semibold text-gray-400">
                        {walletData.cashBalance ? (
                          <span
                            dir="rtl"
                            className="flex items-center justify-center gap-2 text-xl font-bold text-sidebar"
                          >
                            {walletData.cashBalance}
                            <span>
                              {AppConfig.currency.icon("currentcolor", 18)}
                            </span>
                          </span>
                        ) : (
                          <p className="text-center text-[23px] font-semibold text-gray-400">
                            {t(
                              "walletSettings.noBalance"
                            )}
                          </p>
                        )}
                      </p>
                    </div>
                    <div className="card-footer text-center">
                      <Link
                        to="/settings/store-cash-history"
                        className="hover:text-colors-primary mt-auto flex items-center justify-center gap-1 text-[15px] text-sidebar underline transition-colors duration-100"
                      >
                        <FaStore /> {t("walletSettings.transferHistory")}
                      </Link>
                    </div>
                  </div>

                  {/* Cashier sales */}
                  {isCashierInstalledAndActive && (
                    <div className="store-cash main-border h-[260px] w-[420px] space-y-6 rounded-md border border-gray-300 px-4 py-4">
                      <div className="card-header items flex items-center gap-3 text-gray-400">
                        <FaCashRegister className="text-[19px]" />
                        <h3 className="m-0 font-semibold text-gray-400">
                          {t("walletSettings.cashierSales")}
                        </h3>
                      </div>
                      <div className="card-body flex flex-col items-center justify-center gap-2">
                        <FaCashRegister className="text-[35px] text-gray-400" />
                        <p className="p-3 text-center text-[30px] font-semibold text-gray-400">
                          {walletData.cashierBalance ? (
                            <span
                              dir="rtl"
                              className="flex items-center justify-center gap-2 text-xl font-bold text-sidebar"
                            >
                              {walletData.cashierBalance}
                              <span>
                                {AppConfig.currency.icon("currentcolor", 18)}
                              </span>
                            </span>
                          ) : (
                            <p className="text-center text-[23px] font-semibold text-gray-400">
                              {t(
                                "walletSettings.noBalance"
                              )}
                            </p>
                          )}
                        </p>
                      </div>
                      <div className="card-footer text-center">
                        <Link
                          to="/cashierhistory"
                          className="hover:text-colors-primary mt-auto flex items-center justify-center gap-1 text-[15px] text-sidebar underline transition-colors duration-100"
                        >
                          <FaStore /> {t("walletSettings.totalSales")}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
          <section className="my-10">
            {/* may need remove  md:w-[90%]*/}
            <div className="w-full whitespace-nowrap rounded-md border bg-[#FCFCFC] p-5 ">
              <h1 className="flex items-center gap-2 font-semibold text-slate-500">
                {t("walletSettings.transferHistory")}{" "}
              </h1>
              <div className="mt-5">
                <div className="thin-scrollbar relative overflow-x-auto">
                  {/* Table */}

                  <table className="w-full text-left text-sm text-gray-500 rtl:text-right">
                    <thead className="bg-[#F2F5F7] uppercase text-gray-400">
                      <tr>
                        <th scope="col" className="px-2 py-3">
                          {t("walletSettings.transactionNumber")}
                        </th>
                        <th scope="col" className="px-2 py-3">
                          {t("walletSettings.status")}
                        </th>
                        <th scope="col" className="px-2 py-3">
                          {t("walletSettings.transactionAmount")}
                        </th>
                        <th scope="col" className="px-2 py-3">
                          {t("walletSettings.transactionDate")}
                        </th>
                        <th scope="col" className="px-2 py-3">
                          {t("walletSettings.attachment")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutHistory?.length > 0 ? (
                        payoutHistory.map((transaction, index) => (
                          <tr key={index} className="bg-[#FFFFFF]">
                            <td className="px-2 py-4">{transaction?._id}</td>
                            <td className="px-2 py-4">
                              <span className={`px-3 py-[6px] capitalize font-semibold text-xs text-gray-800 rounded-full ${payoutsStatusConfigs[transaction?.payoutInfo?.status || "paid"].color}`}>
                                {lang === "en" ? payoutsStatusConfigs[transaction?.payoutInfo?.status || "paid"].labelEn : payoutsStatusConfigs[transaction?.payoutInfo?.status || "paid"].labelAR}</span>
                            </td>
                            <td dir="rtl" className="px-2 py-4">
                              {transaction.amount
                                ? `${formatMoney(transaction?.amount)} `
                                : "-"}
                              {transaction.amount && AppConfig.currency.icon()}
                            </td>
                            <td className="px-2 py-4">
                              {transaction?.time
                                ? new Date(
                                  transaction?.time * 1000,
                                ).toLocaleDateString("en-GB") // dd/mm/yyyy format
                                : "-"}
                            </td>
                            <td className="px-2 py-4">
                              {transaction.attachment && (
                                <a
                                  href={transaction.attachment}
                                  download
                                  target="_blank"
                                  className="text-sidebar"
                                >
                                  <FaDownload size={18} />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4} // Update colspan to 4 since we added a new column
                            className="px-2 py-4 text-center text-slate-500"
                          >
                            {t("walletSettings.noData")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      <PaginationControls
        pageNum={pageNum}
        setPageNum={handleSetPageNumber}
        totalItems={totalItems}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};

export default Wallet;
